import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6e3n8bv53dtlm7j8ut0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 3000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <section id="contact" className="py-32 bg-white">
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-2 gap-16">
          {/* Left - Contact Info */}
          <div>
            <h2 className="text-5xl font-light text-gray-900 mb-12" style={{ fontFamily: 'Georgia, serif' }}>Get in Touch</h2>
            <p className="text-base text-gray-600 mb-12 leading-relaxed">
              Have questions about our products or need support? We're here to help. Reach out to our team and we'll get back to you as soon as possible.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-map-pin-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Address</p>
                  <p className="text-sm text-gray-600">Vijenac Ive Marinkovica 2</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-building-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">City</p>
                  <p className="text-sm text-gray-600">Ogulin</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-mail-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Zip</p>
                  <p className="text-sm text-gray-600">47300</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-global-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Country</p>
                  <p className="text-sm text-gray-600">Croatia</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-mail-send-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Email</p>
                  <p className="text-sm text-gray-600">support@ogindoo.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-phone-line text-lg text-gray-700"></i>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 mb-1">Number</p>
                  <p className="text-sm text-gray-600">+1 6812558616</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div>
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-900 mb-2">Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 text-sm resize-none"
                  placeholder="Tell us how we can help..."
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {status === 'submitting' ? 'Sending...' : status === 'success' ? 'Message Sent!' : status === 'error' ? 'Failed. Try Again' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
